import { LegacyRef, useEffect, useRef, useState } from "react";
import { ReactComponent as MenuIcon } from "../../assets/img/grid-menu.svg";
import OffCanvas from "../../components/offCanvas";
import CopyLinkButton from "../../components/CopyLinkButton";
import AddCareNote from "../../Care Note/AddCareNote";
import {
  FilterCriteria,
  GridDataRequest,
  GridDataResponse,
  MemberFormInfo,
} from "clinical-forms-experience-client";
import {
  CellClickedEvent,
  ColDef,
  ValueFormatterParams,
} from "ag-grid-community";
import { GenericGrid } from "../../grid/GenericGrid";
import { formatDateTime } from "../../helper/dateFormatter";
import CareNoteHeader from "./CareNoteHeader";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setPageTitle } from "../../store/slices/pageTitleSlice";
import { createGridApi } from "../../api-helpers/GridApiHelper";
import { createClinicalFormsApi } from "../../api-helpers/ClinicalFormsApiHelper";
import { ClinicalFormsConstants } from "../ClinicalForms/ClinicalFormsConstants";
import Error404 from "../error-404";
import { AgGridReact } from "ag-grid-react";
import { SortCriteria } from "patients-experience-client";
import { NEW_CARE_NOTE_FORM_PARAM, NEW_CARE_NOTE_PARAM } from "./careNoteLinks";

export const CareNoteGrid = () => {
  const gridApi = createGridApi();
  const clinicalFormsApi = createClinicalFormsApi();

  // :memberFormId is the deep link — present whenever a specific note is open.
  const { patientId, memberFormId } = useParams<{
    patientId: string;
    memberFormId: string;
  }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // ?new=1 asks us to open the Add drawer on arrival (see careNoteLinks.ts).
  const openNewNote = searchParams.get(NEW_CARE_NOTE_PARAM) === "1";
  // ...optionally on a given care note type (e.g. a journey module).
  const newNoteFormId = searchParams.get(NEW_CARE_NOTE_FORM_PARAM) ?? undefined;
  const careNotesPath = `/patients/${patientId}/care-note`;

  const [editingMemberForm, setEditingMemberForm] = useState<
    MemberFormInfo | undefined
  >();
  const [gridReloadKey, setGridReloadKey] = useState(0);
  const [filters, setFilters] = useState<FilterCriteria[]>([]);
  const [offCanvasTitle, setOffCanvasTitle] = useState("");
  const [offCanvasShow, setOffCanvasShow] = useState(false);
  // The care note type chosen from the "Add Care Note" dropdown, pre-selected in the modal.
  const [addFormId, setAddFormId] = useState<string | undefined>();
  // Bumped on every open and used as <AddCareNote>'s key, so each open is a distinct instance
  // with fresh state. (react-bootstrap reuses the existing instance if the modal is reopened
  // within its ~300ms close-transition window; a per-open key makes the lifecycle explicit.)
  const [openSeq, setOpenSeq] = useState(0);
  // Set when :memberFormId doesn't resolve to a note we can load.
  const [notFound, setNotFound] = useState(false);
  const dispatch = useDispatch();
  const sortCriteria: SortCriteria = {
    propertyName: "addedOn",
    descending: true,
  };

  useEffect(() => {
    dispatch(setPageTitle("Care Notes: Patient Profile | Spire360"));
  }, []);

  // Viewing a note is driven entirely by the URL
  useEffect(() => {
    setNotFound(false);

    if (!memberFormId) {
      return;
    }

    const openLightbox = () => {
      setAddFormId(undefined);
      setOffCanvasTitle("View Care Note");
      setOpenSeq((s) => s + 1);
      setOffCanvasShow(true);
    };

    // Open straight away
    const openedFromRow = editingMemberForm?.id === memberFormId;
    if (openedFromRow) {
      openLightbox();
    }

    let cancelled = false;
    const loadNote = async () => {
      try {
        const memberForm = (
          await clinicalFormsApi.clinicalFormsControllerGetMemberForm(
            memberFormId,
          )
        ).data;
        if (cancelled) {
          return;
        }

        setEditingMemberForm(memberForm);
        if (!openedFromRow) {
          openLightbox();
        }
      } catch (error) {
        console.error("Error while loading the linked care note:", error);
        if (cancelled) {
          return;
        }
        setOffCanvasShow(false);
        setNotFound(true);
      }
    };
    loadNote();

    return () => {
      cancelled = true;
    };
  }, [memberFormId]);

  const reloadGrid = () => {
    setGridReloadKey(gridReloadKey + 1);
  };

  const handleOffCanvasVisibility = (show: boolean) => {
    setOffCanvasShow(show);
    if (!show && memberFormId) {
      navigate(careNotesPath, { replace: true });
    }
  };

  const handleOffCanvasShow = (comp: string, formId?: string) => {
    if (comp === "addCareNote") {
      if (memberFormId) {
        navigate(careNotesPath, { replace: true });
      }
      setEditingMemberForm(undefined);
      setAddFormId(formId);
      setOffCanvasTitle("Add Care Note");
      setOpenSeq((s) => s + 1);
      setOffCanvasShow(true);
    }
  };

  // Arriving with ?new=1 opens the Add drawer on the requested care note type
  // (&form=<id>) or else the "Appointment" one, then drops the flag so closing the drawer or stepping back through history does
  // not reopen it.
  //
  // The type id matters: AddCareNote renders "Select a care note type to begin" until
  // it has one, so opening without it gives an empty shell. Types are per-environment
  // server data, so the id is resolved by title the same way ProgressNotes.tsx does.
  useEffect(() => {
    if (!openNewNote) {
      return;
    }

    let cancelled = false;

    const resolveAppointmentCareNoteId = async (): Promise<string | undefined> => {
      try {
        const forms = (
          await clinicalFormsApi.clinicalFormsControllerGetClinicalForms(
            ClinicalFormsConstants.careNoteType,
          )
        ).data;
        return forms.find(
          (form) => form.title === ClinicalFormsConstants.appointmentCareNote,
        )?.id;
      } catch (error) {
        console.error("Error resolving the appointment care note type:", error);
        return undefined;
      }
    };

    const openNewCareNote = async () => {
      const formId = newNoteFormId ?? (await resolveAppointmentCareNoteId());

      if (cancelled) {
        return;
      }

      // A missing id still opens the drawer -- the clinician picks the type there.
      handleOffCanvasShow("addCareNote", formId);
      navigate(careNotesPath, { replace: true });
    };

    openNewCareNote();

    return () => {
      cancelled = true;
    };
  }, [openNewNote]);

  // Only a note that exists on the server can be linked to
  const openNoteStatus = editingMemberForm?.status?.toLowerCase();
  const canCopyLink =
    !!editingMemberForm?.id &&
    (openNoteStatus === ClinicalFormsConstants.draftStatus ||
      openNoteStatus === ClinicalFormsConstants.submittedStatus);

  const copyLinkAction = canCopyLink ? (
    <CopyLinkButton
      value={`${window.location.origin}${careNotesPath}/${editingMemberForm!.id}`}
      title="Copy a link to this care note"
    />
  ) : undefined;

  const CustomButtonRenderer = () => {
    return (
      <div className="cursor-pointer">
        <MenuIcon />
      </div>
    );
  };

  const colDefs: ColDef[] = [
    {
      field: "careNoteType",
      headerName: "Care Note Type",
      minWidth: 350,
      maxWidth: 380,
      pinned: "left",
      flex: 1,
      sortable: true,
    },
    {
      field: "chartingDateTime",
      headerName: "Charting Date/Time",
      minWidth: 170,
      pinned: "left",
      maxWidth: 250,
      sortable: true,
      valueFormatter: (params: ValueFormatterParams) =>
        formatDateTime(params.data.chartingDateTime),
    },
    {
      headerName: "Coach",
      minWidth: 230,
      maxWidth: 280,
      field: "coachName",
      sortable: true,
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 120,
      maxWidth: 200,
      sortable: true,
      valueFormatter: (params: ValueFormatterParams) =>
        params.data.status
          ?.split(" ")
          .map(
            (word: string) =>
              word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
          )
          .join(" "),
    },
    {
      field: "addedBy",
      headerName: "Added By",
      minWidth: 180,
      sortable: true,
    },

    {
      field: "addedOn",
      headerName: "Added On",
      minWidth: 170,
      maxWidth: 250,
      sortable: true,
      valueFormatter: (params: ValueFormatterParams) =>
        formatDateTime(params.data.addedOn),
    },

    {
      cellRenderer: CustomButtonRenderer,
      maxWidth: 50,
      pinned: "right",
      sortable: false,
      onCellClicked: (event: CellClickedEvent<MemberFormInfo, any>) => {
        if (!event.data?.id) {
          return;
        }
        // Seed the note so the lightbox can open without waiting on the network, then let the URL do the opening — that keeps the address bar in step with what's on screen.
        setEditingMemberForm(event.data);
        navigate(`${careNotesPath}/${event.data.id}`);
      },
    },
  ];

  const loadData = async (
    request: GridDataRequest,
  ): Promise<GridDataResponse> => {
    return (await gridApi.gridControllerGetCareNotesGridData(request)).data;
  };

  if (notFound) {
    return <Error404 statusCode={404} errMessage="Care note not found" />;
  }

  return (
    <>
      <main className="main">
        <CareNoteHeader
          patientId={patientId}
          offCanvasShowCallback={handleOffCanvasShow}
          onUpdateFilters={setFilters}
        />
        <div className="d-flex px-3 px-md-4 px-lg-5 d-flex align-items-stretch">
          <div className="flex-fill">
            <div className="py-5">
              <GenericGrid
                gridId={`care-note-grid`}
                colDefs={colDefs}
                dataProvider={loadData}
                filters={filters}
                key={gridReloadKey}
                sortCriteria={sortCriteria}
              ></GenericGrid>
            </div>
          </div>
        </div>
      </main>
      <OffCanvas
        title={offCanvasTitle}
        titleAction={copyLinkAction}
        offCanvasShow={offCanvasShow}
        setOffCanvasShow={handleOffCanvasVisibility}
        placement="top"
        backdrop="static"
        className="lightbox-offcanvas care-note-form-offcanvas"
      >
        <AddCareNote
          key={openSeq}
          patientId={patientId!}
          setOffCanvasShow={handleOffCanvasVisibility}
          editingMemberFormId={editingMemberForm?.id}
          initialFormId={addFormId}
          reloadGrid={reloadGrid}
          // onUpdateCareNotes={loadMemberForms}
        />
      </OffCanvas>
    </>
  );
};
